import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getSeoMeta } from '@/seo/meta';
import { getJsonLd } from '@/seo/jsonld';

// 集中式客户端 SEO 同步：
// 1. 预渲染（SSG）已在静态 HTML 里写入正确的 title/meta/canonical/og/hreflang/JSON-LD；
// 2. 客户端 React 接管后，随站内路由切换（SPA 导航）同步更新 <head>，
//    确保不沿用前一页的 canonical/title/JSON-LD、不产生重复标签。

function ensureMeta(attr: 'name' | 'property', key: string, content: string) {
  const sel = attr === 'name' ? `meta[name="${key}"]` : `meta[property="${key}"]`;
  let el = document.head.querySelector(sel) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

function removeMeta(attr: 'name' | 'property', key: string) {
  const sel = attr === 'name' ? `meta[name="${key}"]` : `meta[property="${key}"]`;
  document.head.querySelectorAll(sel).forEach((n) => n.remove());
}

function ensureCanonical(href: string) {
  let el = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
}

function applySeo(pathname: string) {
  const meta = getSeoMeta(pathname);
  const schemas = getJsonLd(pathname);

  document.title = meta.title;

  ensureMeta('name', 'description', meta.description);
  if (meta.keywords) {
    ensureMeta('name', 'keywords', meta.keywords);
  } else {
    removeMeta('name', 'keywords');
  }

  ensureCanonical(meta.canonicalUrl || meta.url);

  if (meta.robots) {
    ensureMeta('name', 'robots', meta.robots);
  } else {
    removeMeta('name', 'robots');
  }

  ensureMeta('property', 'og:title', meta.title);
  ensureMeta('property', 'og:description', meta.description);
  ensureMeta('property', 'og:url', meta.url);
  ensureMeta('property', 'og:type', meta.type || 'website');
  if (meta.image) {
    ensureMeta('property', 'og:image', meta.image);
    ensureMeta('property', 'og:image:alt', meta.title);
  } else {
    removeMeta('property', 'og:image');
    removeMeta('property', 'og:image:alt');
  }
  if (meta.ogLocale) {
    ensureMeta('property', 'og:locale', meta.ogLocale);
  }

  ensureMeta('name', 'twitter:title', meta.title);
  ensureMeta('name', 'twitter:description', meta.description);
  if (meta.image) {
    ensureMeta('name', 'twitter:image', meta.image);
  } else {
    removeMeta('name', 'twitter:image');
  }

  // hreflang：先清空历史注入，再按当前页面输出，避免跨页残留
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
  (meta.hreflangs || []).forEach((h) => {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = h.lang;
    link.href = h.url;
    document.head.appendChild(link);
  });

  // JSON-LD：移除上一页注入的脚本后重新写入，避免累积与重复
  document.head.querySelectorAll('script[data-seo-jsonld="true"]').forEach((n) => n.remove());
  schemas.forEach((schema) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', 'true');
    script.textContent = JSON.stringify(schema).replace(/</g, '\\u003c');
    document.head.appendChild(script);
  });
}

export default function SeoHead() {
  const location = useLocation();

  useEffect(() => {
    applySeo(location.pathname);
  }, [location.pathname]);

  return null;
}